import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { IconArrowDown } from '@tabler/icons-react';

import { useBorderColor } from 'src/Components/Solve/hooks';
import {
  selectBackgroundColor,
  selectLegibleElementColor,
} from 'src/Components/Solve/StepWrapper/selectors';
import { useStepContext } from 'src/Components/Solve/StepWrapper/StepWrapper';
import { DropdownItem } from 'src/Components/Solve/types';

interface MultiLevelDropdownProps {
  /** Back button text next to icon */
  backButtonText: string;
  /** Dropdown Values */
  dropdownValues: Array<DropdownItem>;
  /** Function to handle onChange action */
  onChange: (value: string, dropdownLabel: string) => void;
}

const MultiLevelDropdown: React.FC<
  React.PropsWithChildren<MultiLevelDropdownProps>
> = ({ backButtonText, dropdownValues, onChange }) => {
  const theme = useTheme();
  const context = useStepContext();
  const [selected, setSelected] = useState<Array<number>>([]);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [isSelectedFromChild, setIsSelectedFromChild] =
    useState<boolean>(false);
  const legibleElementColor = selectLegibleElementColor(context);
  const hoveredTheme = selectBackgroundColor(context);
  const borderColor = useBorderColor();

  let selectedItems = dropdownValues;

  selected.forEach(index => {
    const dropdownOptions = selectedItems[index]?.dropdown_options;
    if (dropdownOptions) selectedItems = dropdownOptions;
  });

  return (
    <Container
      backgroundColor={theme.palette.colors.white}
      borderColor={borderColor}
    >
      {selected.length > 0 && (
        <BackBtn
          data-testid='multilevelDropdown-backBtn'
          hoveredBackgroundColor={hoveredTheme}
          onClick={e => {
            e.preventDefault();
            const selectedIndexes = [...selected];
            selectedIndexes.pop();
            setSelected(selectedIndexes);
            setIsSelectedFromChild(false);
          }}
          textColor={legibleElementColor}
        >
          {backButtonText} <IconArrowDown />
        </BackBtn>
      )}
      <List>
        {selectedItems.map((item: DropdownItem, index: number) => {
          const dropdownOptions = item.dropdown_options;
          const dropdownOptionsExist =
            dropdownOptions && dropdownOptions.length > 0;
          if (isSelectedFromChild && selectedValue !== item.value) return null;
          return (
            <ListItem
              backgroundColor={theme.palette.colors.white}
              hoveredBackgroundColor={theme.palette.colors.grey[100]}
              hoveredTextColor={theme.palette.text.secondary}
              isSelected={selectedValue === item.value}
              key={item.label}
              textColor={theme.palette.text.primary}
            >
              <ListButton
                onClick={() => {
                  if (!selectedItems[index]?.dropdown_options?.length) {
                    onChange(item.value, item.label);
                    setSelectedValue(item.value);
                    setIsSelectedFromChild(selected.length > 0);
                  } else setSelected([...selected, index]);
                }}
              >
                {item.label}
                {dropdownOptionsExist && <IconArrowDown />}
              </ListButton>
            </ListItem>
          );
        })}
      </List>
    </Container>
  );
};

export default MultiLevelDropdown;

const Container = styled('div')<{
  backgroundColor: string;
  borderColor: string;
}>`
  background-color: ${props => props.backgroundColor};
  border-radius: 4px;
  border: 1px solid ${props => props.borderColor};
  max-height: 160px;
  overflow-y: auto;
  margin: 8px 0;
`;

const BackBtn = styled('button')<{
  hoveredBackgroundColor: string;
  textColor: string;
}>`
  outline: none;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${props => props.textColor};
  width: 100%;
  height: 40px;
  padding: 11px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:hover,
  &:focus {
    background-color: ${props => props.hoveredBackgroundColor};
  }
`;

const List = styled('ul')`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled('li')<{
  backgroundColor: string;
  hoveredBackgroundColor: string;
  hoveredTextColor: string;
  isSelected?: boolean;
  textColor: string;
}>`
  color: ${props =>
    props.isSelected ? props.hoveredTextColor : props.textColor};
  background-color: ${props =>
    props.isSelected ? props.hoveredBackgroundColor : props.backgroundColor};
  cursor: pointer;
  padding: 11px 10px;
  min-height: 40px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  &:hover,
  &:focus-within {
    background-color: ${props => props.hoveredBackgroundColor};
    color: ${props => props.hoveredTextColor};
  }
`;

const ListButton = styled('button')`
  background-color: transparent;
  border: none;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  cursor: pointer;
  outline: none;
`;
